/**
 * ``useProducerNames`` — a ``producer_id → display_name`` resolver for the
 * Detections + Alerts list panels (REQ-UI-110).
 *
 * Detections and Alerts rows carry a raw ``producer_id`` UUID (their SAPIENT
 * payloads name the source by id, not name). REQ-UI-110 requires those rows to
 * show the **platform display name** — and to make it a sort field
 * (``platform_name``). The names live in the producers inventory the Platforms
 * panel already consumes (``GET /api/query/producers`` via :func:`useProducers`),
 * so this hook joins on ``producer_id`` client-side (the same merge-on-id pattern
 * :class:`PlatformsPanel` uses for live status) rather than asking the BFF to
 * denormalise the name onto every detection / alert.
 *
 * # Why a shared hook
 *
 * Both panels need the identical resolver — for the row label *and* the
 * ``platform_name`` sort comparator — so the join + the id-fallback live once
 * (CLAUDE.md §4.4 — no duplicated logic). The returned :type:`ProducerNameIndex`
 * exposes a memoised ``nameFor(id)`` that returns the display name or, when the
 * producer is not (yet) in the inventory, the raw id — so a detection from a
 * just-sighted producer the inventory poll hasn't picked up still renders
 * something legible (CLAUDE.md §8.1 — partial data over a blank), and the
 * ``platform_name`` sort falls back to id-order for that row rather than
 * dropping it.
 *
 * # Freshness
 *
 * Reuses :func:`useProducers` (a polled global inventory with TanStack Query
 * structural sharing), so the name index updates as producers register without
 * any extra fetch, and an unchanged inventory poll yields a referentially-stable
 * index (no row re-render / no re-sort churn). The hook never gates rendering on
 * the producers query — a missing / in-flight / errored inventory simply yields
 * id-fallback names, exactly like the Platforms panel keeps rendering when its
 * supplementary status query is absent.
 *
 * Cross-references:
 *   - REQ-UI-110 (platform-name join + sort field) / REQ-UI-051 (the join must
 *     not disturb id-keyed selection — it only affects the label + sort key).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - @/platforms/use-producers (the inventory source).
 */
import { useMemo } from "react";
import { useProducers } from "@/platforms/use-producers";

/**
 * A resolved ``producer_id → display_name`` index. ``nameFor`` is the only
 * method callers need; ``hasName`` lets a caller distinguish a resolved name
 * from the id-fallback when that matters (the panels don't, but it keeps the
 * fallback explicit for tests + future callers).
 */
export interface ProducerOption {
  /** The ``producer_id`` a predicate or a selection carries. */
  readonly id: string;
  /** The display name the operator reads. */
  readonly name: string;
}

export interface ProducerNameIndex {
  /**
   * The display name for ``producerId``, or the raw ``producerId`` itself when
   * the producer is not in the inventory (a just-sighted producer the poll
   * hasn't picked up). Never throws; always returns a non-empty string the row
   * can show + the ``platform_name`` comparator can sort on.
   */
  readonly nameFor: (producerId: string) => string;
  /** Whether ``producerId`` resolved to a real display name (vs. the id-fallback). */
  readonly hasName: (producerId: string) => boolean;
  /**
   * Every known producer as a pickable ``{id, name}``, sorted by name
   * (REQ-UI-188).
   *
   * The Query builder's ``producer_id`` operand is a picker over this rather
   * than a free-text UUID field: the results table has always rendered that
   * column as the display name (``query-columns.tsx``), so an operator could
   * read "BD-RDR-01" in a result and still have no way to *filter* for it
   * without knowing its registry UUID. Same inventory, same join, so the name
   * that comes back is the name that was picked.
   *
   * Empty — never undefined — while the inventory is absent, in flight or
   * errored, so a caller can map over it unconditionally.
   */
  readonly options: readonly ProducerOption[];
}

/**
 * Build the ``producer_id → display_name`` index from the producers inventory.
 *
 * @returns A memoised :type:`ProducerNameIndex`; stable across inventory polls
 *     that return an unchanged producer set (TanStack structural sharing).
 *
 * Cross-references:
 *   - REQ-UI-110 / REQ-UI-026.
 */
export function useProducerNames(): ProducerNameIndex {
  // The FULL inventory, including rows the Platforms list excludes: a Provider's own
  // node is not a platform, but records attributed to one still need a legible label.
  // Chiefly alerts ingested before alerts carried a per-sensor identity carrier —
  // those were attributed to the aggregator's node and keep that attribution (no
  // backfill), so without this they would render as raw UUIDs.
  const query = useProducers({ includeProviderNodes: true });
  const items = query.data?.items;

  return useMemo<ProducerNameIndex>(() => {
    const byId = new Map<string, string>();
    for (const item of items ?? []) {
      byId.set(item.producer_id, item.display_name);
    }
    // Built here rather than by each caller so the picker and the row labels
    // can never disagree about what a producer is called, and so the sort is
    // computed once per inventory change instead of once per render.
    const options: readonly ProducerOption[] = [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return {
      nameFor: (producerId) => byId.get(producerId) ?? producerId,
      hasName: (producerId) => byId.has(producerId),
      options,
    };
    // ``items`` is referentially stable across no-change polls (structural
    // sharing), so the index — and therefore the row labels + sort order —
    // only recompute when the producer set actually changes.
  }, [items]);
}
